import axiosInstance from "../../../config/axios";

/**
 * Sales / checker pre-book APIs (S1 – S10).
 *
 * Each call mirrors the retailer pre-book API with the customer passed in the
 * path (:customerId) and in the `customer` header — exactly like the existing
 * sales order APIs. The header must match the customer selected in the sales
 * session, otherwise the API answers 409.
 */

export interface SalesPreBook {
  id: number;
  title: string;
  description: string | null;
  startDate: string; // YYYY-MM-DD
  endDate: string;
  showPrice: boolean;
  productCount: number;
}

const customerHeaders = (customerId: string | number) => ({
  headers: { customer: String(customerId) },
});

// S1 — GET /sales/pre-book/available?C_Number=1001 → { data: { available } }
// Does not need the customer header, so it can be called before a customer is picked.
export const getSalesPreBookAvailability = async (customerNumber: string | number) => {
  const response = await axiosInstance.get("/sales/pre-book/available", { params: { C_Number: customerNumber } });
  return response.data;
};

// Should a delivery charge apply to pre-book orders for that customer? -- { data: { allowed: boolean } }
// true  -> fetch the normal delivery charge and apply it
// false -> delivery charge is 0
export const getSalesPreBookDeliveryChargeAllowed = async (customerId?: string | number) => {
  const response = await axiosInstance.get(
    "/sales/pre-book/delivery-charge-allowed",
    customerId ? customerHeaders(customerId) : undefined
  );
  return response.data;
};

// S2 — pre-books open to that customer now → { data: SalesPreBook[] }
export const getSalesPreBooks = async (customerId: string | number) => {
  const response = await axiosInstance.get(`/sales/pre-book/list/${customerId}`, customerHeaders(customerId));
  return response.data;
};

// S3 — products of one pre-book for that customer. Same body / response as /sales/getInventoryItems/:customerId + `preBook`.
export const getSalesPreBookProducts = async (
  preBookId: number | string,
  customerId: string | number,
  params: any,
  signal?: AbortSignal
) => {
  const payload = {
    page: 1,
    limit: 10,
    ...params,
  };
  const response = await axiosInstance.post(`/sales/pre-book/${preBookId}/inventory/${customerId}`, payload, {
    ...customerHeaders(customerId),
    ...(signal !== undefined && { signal }),
  } as any);
  return response.data;
};

// S4 — body = R4 body (+ preBookId). Stored as placed by the sales rep.
export const addToSalesPreBookCart = async (preBookId: number | string, customerId: string | number, params: any) => {
  const response = await axiosInstance.post(
    `/sales/pre-book/cart/${customerId}/add`,
    { ...params, preBookId: Number(preBookId) },
    customerHeaders(customerId)
  );
  return response.data;
};

// S5 — cart of one pre-book for that customer (same prices / flags the retailer sees)
export const getSalesPreBookCartItems = async (preBookId: number | string, customerId: string | number) => {
  const response = await axiosInstance.get(`/sales/pre-book/cart/${customerId}/items`, {
    ...customerHeaders(customerId),
    params: { preBookId: Number(preBookId) },
  });
  return response.data;
};

// S6
export const updateSalesPreBookCartItem = async (cartItemId: string | number, params: any, customerId: string | number) => {
  const response = await axiosInstance.put(
    `/sales/pre-book/cart/${customerId}/items/${cartItemId}`,
    params,
    customerHeaders(customerId)
  );
  return response.data;
};

// S7
export const removeFromSalesPreBookCart = async (cartItemId: string | number, customerId: string | number) => {
  const response = await axiosInstance.delete(
    `/sales/pre-book/cart/${customerId}/items/${cartItemId}`,
    customerHeaders(customerId)
  );
  return response.data;
};

// S8 — empties that pre-book's cart only
export const clearSalesPreBookCart = async (preBookId: number | string, customerId: string | number) => {
  const response = await axiosInstance.delete(`/sales/pre-book/cart/${customerId}/clear`, {
    ...customerHeaders(customerId),
    params: { preBookId: Number(preBookId) },
  });
  return response.data;
};

// S9 — { cartItemIds: [] } → data: { removed }
export const removeMultipleSalesPreBookCartItems = async (customerId: string | number, cartItemIds: number[]) => {
  const response = await axiosInstance.post(
    `/sales/pre-book/cart/${customerId}/removeMultipleItems`,
    { cartItemIds },
    customerHeaders(customerId)
  );
  return response.data;
};

// S10 — body = the existing POST /sales/placeOrder/:customerId body + preBookId.
// hasDiscount / discountAmount / discountPrice / points work as today. The order is saved as a request
// for the distributor — data: { requestNumber: "PBO-00012", status: "PENDING", preBookOrder } — and is
// created (Order_Type = 1) only when they approve it.
export const placeSalesPreBookOrder = async (preBookId: number | string, customerId: string | number, params: any) => {
  const response = await axiosInstance.post(
    `/sales/pre-book/placeOrder/${customerId}`,
    { ...params, preBookId: Number(preBookId) },
    {
      headers: {
        "Content-Type": "application/json",
        customer: String(customerId),
        "is-web-order": "true",
      },
    }
  );
  return response.data;
};

// S11 — that customer's order requests on one pre-book ("Your orders" tab) → { data: PreBookOrder[] }
export const getSalesPreBookOrders = async (preBookId: number | string, customerId: string | number) => {
  const response = await axiosInstance.get(`/sales/pre-book/${preBookId}/orders/${customerId}`, customerHeaders(customerId));
  return response.data;
};

// S12 — one order request with its lines → { data: PreBookOrder & { details } }
export const getSalesPreBookOrderById = async (orderId: number | string, customerId: string | number) => {
  const response = await axiosInstance.get(`/sales/pre-book-orders/${orderId}/${customerId}`, customerHeaders(customerId));
  return response.data;
};
